import { useEffect, useMemo, useRef } from 'react';
import {
  FlexColumn,
  FormField,
  Link,
  Alert,
  TextField,
} from '@elementary/common-ui/components';
import { UseFormReturn } from 'react-hook-form';
import { CodeProjectItem, DbtProject, VersionControl } from 'shared/types';
import { Typography } from '@mui/material';
import { isValidUrl } from 'shared/utils/validators';
import { IntegrationError } from '../../../../types';
import { RepositoryFormProvider } from './RepositoryFormContext';
import SharedTokenField from './SharedTokenField';
import DbtProjectForm from './DbtProjectForm';
import SettingsSection from './SettingsSection';

type SharedTokenForm = {
  sharedToken: string;
};

type ProjectForm = {
  repository: string;
  branch: string;
  projectDir: string;
};

type Props = {
  dbtProjects: DbtProject[];
  selectedVersionControl: VersionControl | undefined;
  sharedTokenForm: UseFormReturn<SharedTokenForm>;
  existingCodeProjects?: CodeProjectItem[];
  onProjectFormChange: (
    dbtProjectId: string,
    isValid: boolean,
    isDirty: boolean,
    data: ProjectForm,
  ) => void;
  isNewConnection?: boolean;
  error?: IntegrationError;
  isLoading?: boolean;
  enterpriseUrl?: string;
  onEnterpriseUrlChange?: (value: string) => void;
  addLabels: boolean;
  onAddLabelsChange: (value: boolean) => void;
  branchPrefix: string;
  onBranchPrefixChange: (value: string) => void;
  commitMessagePrefix: string;
  onCommitMessagePrefixChange: (value: string) => void;
  requireTicketId: boolean;
  onRequireTicketIdChange: (value: boolean) => void;
};

const DbtProjectsConfigSection = ({
  dbtProjects,
  selectedVersionControl,
  sharedTokenForm,
  existingCodeProjects = [],
  onProjectFormChange,
  isNewConnection = false,
  error,
  isLoading = false,
  enterpriseUrl,
  onEnterpriseUrlChange,
  addLabels,
  onAddLabelsChange,
  branchPrefix,
  onBranchPrefixChange,
  commitMessagePrefix,
  onCommitMessagePrefixChange,
  requireTicketId,
  onRequireTicketIdChange,
}: Props) => {
  const isEdit = existingCodeProjects.length > 0;
  const errorRef = useRef<HTMLDivElement>(null);

  const enterpriseUrlError = useMemo(() => {
    if (enterpriseUrl && !isValidUrl(enterpriseUrl)) {
      return 'This is not a valid url';
    }
    return undefined;
  }, [enterpriseUrl]);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [error]);

  const showTokenField =
    isEdit && !isNewConnection && selectedVersionControl !== 'Bitbucket';
  const showEnterpriseUrlField =
    isEdit &&
    !isNewConnection &&
    ['GitHub', 'GitLab'].includes(selectedVersionControl || '');
  const showConnectionDetails = showTokenField || showEnterpriseUrlField;

  return (
    <RepositoryFormProvider isLoading={isLoading}>
      <FlexColumn sx={{ gap: 3 }}>
        <FlexColumn>
          <Typography variant="body2">
            Connect your dbt project to a code repository for seamless
            collaboration and enhanced version control.
          </Typography>
          <Typography variant="body2">
            Need help? Check out{' '}
            <Link
              target="_blank"
              href="https://docs.elementary-data.com/cloud/integrations/code-repo/connect-code-repo"
            >
              docs
            </Link>{' '}
            for more info
          </Typography>
        </FlexColumn>

        {error && (
          <div ref={errorRef}>
            <Alert severity="error" style={{ fontWeight: 'bold' }}>
              {error.message}
              {error.integrationError && (
                <>
                  <br />
                  {error.integrationError}
                </>
              )}
            </Alert>
          </div>
        )}

        {showConnectionDetails && (
          <FlexColumn sx={{ gap: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Connection Details
            </Typography>

            {showTokenField && (
              <SharedTokenField
                register={sharedTokenForm.register}
                errors={sharedTokenForm.formState.errors}
                selectedVersionControl={selectedVersionControl}
                isEdit={isEdit}
                isNewConnection={isNewConnection}
              />
            )}

            {showEnterpriseUrlField && (
              <FormField
                title="Enterprise URL"
                description="Leave empty to use the default URL"
                optional
                errorMessage={enterpriseUrlError}
              >
                <TextField
                  value={enterpriseUrl ?? ''}
                  onChange={(e) => onEnterpriseUrlChange?.(e.target.value)}
                  placeholder="https://hostname/api/v3"
                  fullWidth
                  size="large"
                  disabled={isLoading}
                />
              </FormField>
            )}
          </FlexColumn>
        )}

        <FlexColumn sx={{ gap: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Repository Details
          </Typography>

          {dbtProjects.map((dbtProject) => {
            const existingProject = existingCodeProjects.find(
              (cp) => cp.dbt_project_id === dbtProject.id,
            );

            return (
              <DbtProjectForm
                key={dbtProject.id}
                dbtProject={dbtProject}
                existingProject={existingProject}
                onFormChange={onProjectFormChange}
                showProjectName={dbtProjects.length > 1}
              />
            );
          })}
        </FlexColumn>

        <SettingsSection
          selectedVersionControl={selectedVersionControl}
          addLabels={addLabels}
          onAddLabelsChange={onAddLabelsChange}
          branchPrefix={branchPrefix}
          onBranchPrefixChange={onBranchPrefixChange}
          commitMessagePrefix={commitMessagePrefix}
          onCommitMessagePrefixChange={onCommitMessagePrefixChange}
          requireTicketId={requireTicketId}
          onRequireTicketIdChange={onRequireTicketIdChange}
          isLoading={isLoading}
        />
      </FlexColumn>
    </RepositoryFormProvider>
  );
};

export default DbtProjectsConfigSection;
